import { useCallback, useEffect, useRef } from 'react'

import { readKey } from '../Functions'
import { useGigiFormValidation } from '../ValidationContext'

const SKIP_TRAIN_SEARCH_LOCATION =
  'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.skipTrainSearch'

/**
 * Hook condiviso per validazione Zod delle sezioni multi-servizio (hotel, auto, aereo, treno).
 * Accetta un parametro `trigger` opzionale per forzare la rivalidazione quando lo stato
 * viene mutato in-place (stessa reference) da writeKey.
 * @param {Object} params
 * @param {string} params.sectionId - Id sezione nel registro validazione
 * @param {import('react').RefObject} params.containerRef - Ref del contenitore per focus errori
 * @param {Object} params.state - Stato form Gigi
 * @param {number} params.serviceCount - Numero servizi della sezione
 * @param {Function} params.buildSchema - Factory schema Zod (può dipendere da props sezione)
 * @param {Function} params.buildServiceValues - (state, index) => valori per un servizio
 * @param {number} [params.trigger=0] - Contatore incrementale per invalidare la cache di validazione
 * @returns {void}
 */
export const useMultiServiceZodValidation = ({
  sectionId,
  containerRef,
  state,
  serviceCount,
  buildSchema,
  buildServiceValues,
  trigger = 0
}) => {
  const { registerSection, reportErrors } = useGigiFormValidation()

  const validateServices = useCallback(() => {
    const schema = buildSchema()
    const services = Array(serviceCount)
      .fill()
      .map((_, i) => buildServiceValues(state, i))
    let totalErrors = 0
    for (const svc of services) {
      const result = schema.safeParse(svc)
      if (!result.success) totalErrors += result.error.issues.length
    }
    return totalErrors
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, serviceCount, buildSchema, buildServiceValues, trigger])

  const validateRef = useRef(validateServices)
  validateRef.current = validateServices
  const serviceCountRef = useRef(serviceCount)
  serviceCountRef.current = serviceCount

  useEffect(() => {
    return registerSection(sectionId, {
      isValid: async () => {
        if (!serviceCountRef.current) return true
        const errors = validateRef.current()
        reportErrors(sectionId, errors)
        return errors === 0
      },
      focusFirstError: () => {
        const node = containerRef.current?.querySelector('.p-invalid')
        if (node) {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' })
          if (typeof node.focus === 'function') node.focus()
          return true
        }
        return false
      },
      hasErrors: () => validateRef.current() > 0
    })
  }, [registerSection, reportErrors, sectionId, containerRef])

  const errorCount = serviceCount ? validateServices() : 0

  const lastReportedRef = useRef(undefined)
  useEffect(() => {
    if (lastReportedRef.current !== errorCount) {
      lastReportedRef.current = errorCount
      reportErrors(sectionId, errorCount)
    }
  })
}

export { readKey, SKIP_TRAIN_SEARCH_LOCATION }
